/**
 * The dialogs the accounts screens open: the New/Edit Account form, delete
 * confirmation, and the explanation dialog for header actions §5 documents but
 * does not implement.
 *
 * `health_score` / `health_band` have no inputs here, by design — §40 makes
 * them a computed signal (`crm.account.health_score`), and `createAccountBody`/
 * `updateAccountBody` do not accept them; a field a client cannot save is not a
 * field worth rendering disabled.
 */
import { useEffect, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/i;

const LIFECYCLE_STAGE = ['Prospect', 'Customer', 'Renewal', 'Churned', 'Dormant'];

const BLANK = {
  name: '', legal_name: '', parent_id: '', account_type_id: '', industry_id: '',
  website: '', phone: '', email: '', employee_count: '', annual_revenue: '', currency: '',
  lifecycle_stage: 'Prospect', segment: '', territory_id: '', owner_id: '', price_list_id: '',
  billing_address_line1: '', billing_postal_code: '', billing_country_id: '',
};

function validateAccount(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Account name is required.';
  if (form.email && !EMAIL_RE.test(form.email)) errors.email = 'Enter a valid email address.';
  if (form.website && !URL_RE.test(form.website)) {
    errors.website = 'Enter a valid URL starting with http:// or https://.';
  }
  if (form.employee_count !== '' && Number(form.employee_count) < 0) {
    errors.employee_count = 'Employees cannot be negative.';
  }
  if (form.annual_revenue !== '' && Number(form.annual_revenue) < 0) {
    errors.annual_revenue = 'Revenue cannot be negative.';
  }
  return errors;
}

/**
 * Create an account, or edit one. `account` (an `accountShape.toRow()` row)
 * switches the mode.
 */
export function AccountFormModal({ open, onClose, onSubmit, busy, options, account, parentOptions }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const editing = Boolean(account);

  useEffect(() => {
    if (!open) return;
    if (account) {
      const r = account.raw;
      setForm({
        name: account.name ?? '',
        legal_name: account.legalName ?? '',
        parent_id: account.parent?.id ?? '',
        account_type_id: r.account_type?.id ?? '',
        industry_id: r.industry?.id ?? '',
        website: account.website ?? '',
        phone: account.phone ?? '',
        email: account.email ?? '',
        employee_count: account.employees ?? '',
        annual_revenue: account.revenue ?? '',
        currency: account.currency ?? '',
        lifecycle_stage: account.lifecycleStage ?? 'Prospect',
        segment: account.segment ?? '',
        territory_id: r.territory?.id ?? '',
        owner_id: r.owner?.id ?? '',
        price_list_id: r.price_list?.id ?? '',
        billing_address_line1: account.billingAddress?.line1 ?? '',
        billing_postal_code: account.billingAddress?.postal_code ?? '',
        billing_country_id: '', // the detail response names the country, not its id — re-pick to change it
      });
    } else {
      setForm(BLANK);
    }
    setErrors({});
  }, [open, account]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validateAccount(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === '' || v == null) continue;
      if (editing && k === 'parent_id' && v === account.id) continue; // an account cannot be its own parent
      if (k === 'employee_count' || k === 'annual_revenue') payload[k] = Number(v);
      else payload[k] = v;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Account' : 'New Account'}
      size="lg"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="accountForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save account'}
          </button>
        </>
      )}
    >
      <form id="accountForm" onSubmit={submit} noValidate>
        <div className="eyebrow mb-2">Company</div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="Account name" required error={errors.name}>
              <input className="form-control" value={form.name} onChange={set('name')} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Legal name">
              <input className="form-control" value={form.legal_name} onChange={set('legal_name')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Industry">
              <Picker value={form.industry_id} onChange={set('industry_id')} options={options.industries} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Account type">
              <Picker value={form.account_type_id} onChange={set('account_type_id')} options={options.accountTypes} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Parent account" help="Makes this a subsidiary in the hierarchy view.">
              <Picker
                value={form.parent_id} onChange={set('parent_id')}
                options={parentOptions.filter((o) => o.value !== account?.id)} blank="No parent"
              />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Website" error={errors.website}>
              <input className="form-control" type="url" placeholder="https://…" value={form.website} onChange={set('website')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Phone"><input className="form-control" value={form.phone} onChange={set('phone')} /></Field>
          </div>
          <div className="col-md-4">
            <Field label="Email" error={errors.email}>
              <input className="form-control" type="email" value={form.email} onChange={set('email')} />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Segmentation &amp; Assignment</div>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <Field label="Lifecycle stage">
              <select className="form-select" value={form.lifecycle_stage} onChange={set('lifecycle_stage')}>
                {LIFECYCLE_STAGE.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Segment" help="Free text — e.g. Enterprise, Mid-market, SMB.">
              <input className="form-control" value={form.segment} onChange={set('segment')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Employees" error={errors.employee_count}>
              <input className="form-control" type="number" min="0" value={form.employee_count} onChange={set('employee_count')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Annual revenue" error={errors.annual_revenue}>
              <input className="form-control" type="number" min="0" step="1000" value={form.annual_revenue} onChange={set('annual_revenue')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Owner">
              <Picker value={form.owner_id} onChange={set('owner_id')} options={options.owners} blank="Unowned" />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Territory">
              <Picker value={form.territory_id} onChange={set('territory_id')} options={options.territories} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Price list">
              <Picker value={form.price_list_id} onChange={set('price_list_id')} options={options.priceLists} />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Billing address</div>
        <div className="row g-3">
          <div className="col-md-6">
            <Field label="Address line 1">
              <input className="form-control" value={form.billing_address_line1} onChange={set('billing_address_line1')} />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Postal code">
              <input className="form-control" value={form.billing_postal_code} onChange={set('billing_postal_code')} />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Country">
              <Picker value={form.billing_country_id} onChange={set('billing_country_id')} options={options.countries} />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

const Picker = ({ value, onChange, options, blank = 'Not set' }) => (
  <select className="form-select" value={value} onChange={onChange}>
    <option value="">{blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/**
 * Delete, with the reason the database insists on.
 *
 * Refused server-side with `409 account_has_children` while contacts,
 * opportunities or child accounts still point here — the confirm dialog cannot
 * know that in advance, so the error surfaces as a toast naming what is blocking it.
 */
export function DeleteAccountModal({ open, account, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete account"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{account?.name}</strong> ({account?.account_no}).
        {' '}The row stays in the database with <code>deleted_at</code> set, and
        {' '}<code>POST /accounts/{'{id}'}/restore</code> brings it back — but only once
        every contact, opportunity and child account attached to it is moved or removed first.
      </p>
      <Field
        label="Reason" required
        help="Written to the audit trail. delete is requires_reason in iam.app_operation, so the database rejects the write without one."
      >
        <input
          className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Duplicate of AC-00088" autoFocus
        />
      </Field>
    </Modal>
  );
}

/** Why a header action is disabled, in a dialog rather than a tooltip — same pattern as the leads screen. */
export function NotBuiltModal({ open, action, onClose }) {
  if (!action) return null;
  return (
    <Modal
      open={open} onClose={onClose} title={action.title}
      footer={<button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Close</button>}
    >
      <p className="t-sm ink-2">{action.why}</p>
      <div className="t-xs ink-3">Endpoint: <code>{action.endpoint}</code> — {action.section}</div>
    </Modal>
  );
}
